import React from 'react'
import type { FC } from 'react'
import { useSelector } from 'react-redux'
import { createStyles, makeStyles } from '@material-ui/core/styles'
import { Grid } from '@material-ui/core'

import { getUser } from '@homeviews/store'
import { HAS_SOME_DEVELOPMENT_PERMISSIONS } from '@homeviews/users'
import { Card } from '@homeviews/shared-ui'
import {
  Recommendations,
  Reputation,
  Statistic,
  Typography,
} from '@homeviews/analytics'
import type { SummaryStats, Recommendation } from '@homeviews/analytics'

const useStyles = makeStyles(() =>
  createStyles({
    wrapper: {
      marginBottom: '40px',
    },
    statisticsWrapper: {
      marginBottom: '16px',
    },
  }),
)

const numberOfReviewsGoToHref = '/reviews'
const yourResponseRateGoToHref = '/reviews?response=not_responded_to'
const outreachStatisticGoToHref = '/outreach'

type ReputationManagementProps = {
  summaryAllTimeLoading: boolean
  summaryAllTime: SummaryStats
  summaryLastYearLoading: boolean
  summaryLastYear: SummaryStats
  summaryLastQuarterLoading: boolean
  summaryLastQuarter: SummaryStats
}

export const ReputationManagement: FC<ReputationManagementProps> = ({
  summaryAllTimeLoading,
  summaryAllTime,
  summaryLastYearLoading,
  summaryLastYear,
  summaryLastQuarterLoading,
  summaryLastQuarter,
}) => {
  const classes = useStyles()
  const user = useSelector(getUser)

  const numberOfReviewsDataIsSet = () =>
    summaryLastYear?.summary?.reviews !== undefined
  const numberOfReviewsIsGoToDisabled = () =>
    numberOfReviewsDataIsSet() && summaryLastYear.summary.reviews === 0
  const numberOfReviewsValue = () => {
    if (!numberOfReviewsDataIsSet()) return undefined
    return summaryLastYear.summary.reviews.toLocaleString()
  }

  const yourResponseRateDataIsSet = () =>
    summaryLastYear?.summary?.reviews !== undefined &&
    summaryLastYear?.reply_rate?.value !== undefined
  const yourResponseRateIsGoToDisabled = () => {
    if (yourResponseRateDataIsSet() && summaryLastYear.summary.reviews === 0)
      return true
    return !HAS_SOME_DEVELOPMENT_PERMISSIONS(user, [
      'REPLY_TO_REVIEWS',
      'REPLY_TO_REVIEWS_WITHOUT_MODERATION',
    ])
  }
  const yourResponseRateValue = () => {
    if (!yourResponseRateDataIsSet()) return undefined
    return (
      Math.round(parseFloat(summaryLastYear.reply_rate.value)).toString() + '%'
    )
  }
  const yourResponseRateIsAboveBenchmark = () =>
    yourResponseRateDataIsSet() &&
    parseFloat(summaryLastYear.reply_rate.value) > 50
  const yourResponseRateDescription = () => {
    if (!yourResponseRateDataIsSet()) return undefined
    if (yourResponseRateIsAboveBenchmark()) {
      return 'This puts you above the HomeViews recommended manager response rate.'
    } else {
      return 'This puts you below the HomeViews recommended manager response rate.'
    }
  }

  const outreachStatisticIsGoToDisabled = () =>
    !HAS_SOME_DEVELOPMENT_PERMISSIONS(user, [
      'UNDERTAKE_RESIDENT_OUTREACH_REQUESTS',
    ])
  const outreachStatisticDataIsSet = () =>
    summaryLastQuarter?.resident_outreach_development_count?.value !==
      undefined && summaryLastQuarter?.summary?.development_count !== undefined
  const outreachStatisticValue = () => {
    if (!outreachStatisticDataIsSet()) return undefined
    return summaryLastQuarter.resident_outreach_development_count.value.toLocaleString()
  }
  const outreachStatisticIsAboveBenchmark = () =>
    outreachStatisticDataIsSet() &&
    summaryLastQuarter.resident_outreach_development_count.value >=
      summaryLastQuarter.summary.development_count
  const outreachStatisticDescription = () => {
    if (!outreachStatisticDataIsSet()) return undefined
    if (outreachStatisticIsAboveBenchmark()) {
      return 'This puts you above the HomeViews recommended number.'
    } else {
      return 'This puts you below the HomeViews recommended number.'
    }
  }

  const recommendationsData = (): Array<Recommendation> => {
    const recommendationsArr = [] as Array<Recommendation>
    if (!yourResponseRateIsAboveBenchmark()) {
      recommendationsArr.push({
        description:
          "Google research shows that businesses that respond to reviews are seen as 1.7X more trustworthy than businesses who don't. You can respond to resident reviews within the Reviews section of your dashboard.",
        goToHref: yourResponseRateGoToHref,
        goToDisabled: yourResponseRateIsGoToDisabled(),
      })
    }
    if (!outreachStatisticIsAboveBenchmark()) {
      recommendationsArr.push({
        description:
          'We typically see a 5-10% response rate when residents are invited to write a review on HomeViews. Send emails regularly to capture feedback throughout the year and throughout the residents time with you. One resident can write one review per 6 months and we verify them each time.',
        goToHref: outreachStatisticGoToHref,
        goToDisabled: outreachStatisticIsGoToDisabled(),
      })
    }
    return recommendationsArr
  }

  return (
    <Card className={classes.wrapper}>
      <Typography variant="h2">Reputation Management</Typography>
      <Grid container spacing={3}>
        <Grid item xs={4}>
          <Reputation
            isLoading={summaryAllTimeLoading}
            imgSrc="assets/images/homeviews-logo-dark.svg"
            imgAlt="HomeViews logo"
            score={summaryAllTime && summaryAllTime.summary.rating}
            description="This is the all-time star rating"
          />
        </Grid>
      </Grid>
      <Typography variant="h2">
        Review Pipeline
        <span>|</span>
        <span>Over the past 12 months</span>
      </Typography>
      <Grid container spacing={3} className={classes.statisticsWrapper}>
        <Grid item xs={4}>
          <Statistic
            isLoading={summaryLastYearLoading}
            title="Number of reviews"
            goToHref={numberOfReviewsGoToHref}
            goToTrackingEventName="Analytics: Number of reviews: Go to"
            goToIsDisabled={numberOfReviewsIsGoToDisabled()}
            value={numberOfReviewsValue()}
            description=""
          />
        </Grid>
        <Grid item xs={4}>
          <Statistic
            isLoading={summaryLastYearLoading}
            title="Your Response Rate"
            goToHref={yourResponseRateGoToHref}
            goToTrackingEventName="Analytics: Response rate: Go to"
            goToIsDisabled={yourResponseRateIsGoToDisabled()}
            value={yourResponseRateValue()}
            description={yourResponseRateDescription()}
          />
        </Grid>
        <Grid item xs={4}>
          <Statistic
            isLoading={summaryLastQuarterLoading}
            title="Developments where Resident Outreach has been used this quarter"
            goToHref={outreachStatisticGoToHref}
            goToTrackingEventName="Analytics: Resident Outreach: Go to"
            goToIsDisabled={outreachStatisticIsGoToDisabled()}
            value={outreachStatisticValue()}
            description={outreachStatisticDescription()}
          />
        </Grid>
      </Grid>
      <Recommendations
        data={summaryLastYear && summaryLastQuarter && recommendationsData()}
        goToTrackingEventName="Analytics: Reputation Recommendation: Go to"
      />
    </Card>
  )
}
